import { useCallback, useRef } from 'react';

export const useDelayedSave = <TArgs extends unknown[]>(
  func: (...args: TArgs) => void,
  delay = 3000
): [(...args: TArgs) => void, () => void, () => boolean] => {
  const funcRef = useRef(func);
  funcRef.current = func;
  const saveDelay = useRef<ReturnType<typeof setTimeout>>();
  const pendingArgs = useRef<TArgs | null>(null);

  const cancel = useCallback(() => {
    if (saveDelay.current) {
      clearTimeout(saveDelay.current);
      saveDelay.current = undefined;
    }
    pendingArgs.current = null;
  }, []);

  const flush = useCallback(() => {
    const args = pendingArgs.current;
    cancel();
    if (args) funcRef.current(...args);
    return !!args;
  }, [cancel]);

  const save = useCallback(
    (...args: TArgs) => {
      cancel();
      pendingArgs.current = args;
      saveDelay.current = setTimeout(flush, delay);
    },
    [cancel, flush, delay]
  );

  return [save, cancel, flush];
};
